/* eslint-disable max-lines -- Pre-existing violation */
import {
  Alert,
  Button,
  ButtonGroup,
  Card,
  DialogFooter,
  FormButton,
  FormControl,
  FormField,
  NumberInput,
  RadioItem,
  Separator,
} from 'platform/components';
import {Box, Grid, GridItem, Hide, HStack, Show, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';
import {z} from 'zod';

import {RefObject, useImperativeHandle, useRef} from 'react';
import {useWatch, UseFormReturn} from 'react-hook-form';

import {defaultTo, isNil, not} from 'ramda';
import {isFalse, isFalsy, isNotNil, isString} from 'ramda-adjunct';

import {
  GetServiceOrderJobItemWorkApiResponse,
  LabourRateListItem,
  useLazyGetLabourRatesListByWorkTypeQuery,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {
  getPercentageFromDecimal,
  getSourceNameTooltip,
  handleApiError,
  useGetVatRatesOptions,
  useTenant,
  useWorkTypeOptions,
} from '@dms/shared';
import {getLabourTypeLabel, ItemPrice, LabourTypeEnum} from '@dms/shared-aftersales';

import {Nullish, precisionCalculation, suffixTestId, TestIdProps} from 'shared/web';

import {getItemPriceDiscountRate} from '../../../utils/getItemPriceDiscountRate';
import {getLabourRateSellingPrices} from '../../../utils/getLabourRateSellingPrices';
import {getRatioFromAmount} from '../../../utils/getRatioFromAmount';
import {EditLabourItemFormType} from '../schemas/editLabourItemFormSchema';
import {EditLabourItemMechanics} from './EditLabourItemMechanics';

const GOODWILL_RATIO_PERCENTAGE = 100;
const NEUTRAL_GOODWILL_RATIO = 1;

type SelectOption = {label: string; value: string};

interface EditLabourItemFormContentProps extends TestIdProps {
  control: FormControl<EditLabourItemFormType>;
  formApi: UseFormReturn<EditLabourItemFormType>;
  itemData: GetServiceOrderJobItemWorkApiResponse | Nullish;
  labourRatesOptions: SelectOption[];
  labourRates: LabourRateListItem[];
  workItemGroupOptions: SelectOption[];
  canEditCustomPrice: boolean;
  canEditManualDiscount: boolean;
  canReadServiceOrderFinancesLabourItem: boolean;
  isOrderReadOnly: boolean;
  isPatchLoading: boolean;
  itemId: string;
  serviceCaseId: string;
  serviceOrderId: string;
  serviceJobId: string;
  onClose: VoidFunction;
  formRef?: RefObject<{
    submit: () => Promise<boolean>;
    isDirty: boolean;
  } | null>;
}

// eslint-disable-next-line complexity, max-lines-per-function -- Pre-existing violation
export function EditLabourItemFormContent(props: EditLabourItemFormContentProps) {
  'use no memo';
  const tenant = useTenant();
  const [vatOptions] = useGetVatRatesOptions({fullLabel: true});
  const {getOptionsWithSelectedValue} = useWorkTypeOptions();
  const [getLabourRatesByWorkType, {isFetching: isFetchingLabourRates}] =
    useLazyGetLabourRatesListByWorkTypeQuery();
  // Lets an imperatively triggered submit wait for a rate write that is still in flight.
  const pendingLabourRateRef = useRef<Promise<unknown> | null>(null);

  const formValues = useWatch({control: props.control});

  const isTouched = props.formApi.formState.isDirty;

  // Exposes imperative submit/isDirty to the parent dialog row control.
  useImperativeHandle(
    props.formRef,
    () => ({
      submit: async () => {
        await pendingLabourRateRef.current;

        return props.control.submitForm();
      },
      isDirty: isTouched,
    }),
    [props.control, isTouched]
  );

  const itemData = props.itemData;

  const isTimeNormType = itemData?.priceType === 'SVCPRICETYPE_TIME_NORM';
  const isCooperationType = itemData?.priceType === 'SVCPRICETYPE_COOPERATION';
  const isDirectPriceType = itemData?.priceType === 'SVCPRICETYPE_DIRECT_PRICE';
  const isCalculationPriceType = itemData?.priceType === 'SVCPRICETYPE_CALCULATION_PRICE';
  const isSubcontractPriceType = itemData?.priceType === 'SVCPRICETYPE_SUBCONTRACT';

  const goodwillRatio = itemData?.goodwill?.goodwillRatio ?? NEUTRAL_GOODWILL_RATIO;
  const hasGoodwill = goodwillRatio !== NEUTRAL_GOODWILL_RATIO;

  const workTypeOptions = getOptionsWithSelectedValue(itemData?.workType);

  const applyLabourRateSellingPrices = (labourRate: LabourRateListItem | Nullish) => {
    if (props.formApi.getValues('isCustomPrice')) {
      return;
    }

    const sellingPrices = getLabourRateSellingPrices(
      labourRate,
      props.formApi.getValues('quantity')
    );

    if (isNil(sellingPrices)) {
      return;
    }

    props.formApi.setValue('sellingPricePerUnit', sellingPrices.sellingPricePerUnit, {
      shouldDirty: true,
    });
    props.formApi.setValue('originalSellingPricePerUnit', sellingPrices.sellingPricePerUnit, {
      shouldDirty: true,
    });
    props.formApi.setValue('sellingPrice', sellingPrices.sellingPrice, {shouldDirty: true});
  };

  const handleWorkTypeChange = async (workType: string | number | null) => {
    if (not(isTimeNormType) || !isString(workType)) {
      return;
    }

    const labourRateUpdate = getLabourRatesByWorkType({
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.serviceOrderId,
      workType,
    })
      .unwrap()
      .then((labourRates) => {
        // Requests can resolve out of order, so a stale one must not overwrite a newer work type.
        if (props.formApi.getValues('workType') !== workType) {
          return;
        }

        // Sorted with the work type's default first - the response carries no isDefault flag.
        const defaultLabourRate = labourRates?.[0];

        if (isNil(defaultLabourRate)) {
          return;
        }

        props.formApi.setValue('labourRateId', defaultLabourRate.labourRateId, {shouldDirty: true});
        applyLabourRateSellingPrices(defaultLabourRate);
      })
      .catch(handleApiError);

    pendingLabourRateRef.current = labourRateUpdate;

    await labourRateUpdate;

    // A newer change may already own the ref by now.
    if (pendingLabourRateRef.current === labourRateUpdate) {
      pendingLabourRateRef.current = null;
    }
  };

  const handleLabourRateChange = (labourRateId: string | number | null) => {
    if (!isString(labourRateId)) {
      return;
    }

    applyLabourRateSellingPrices(
      props.labourRates.find((labourRate) => labourRate.labourRateId === labourRateId)
    );
  };

  const validateQuantity = (value: number) => {
    const valueToValidate = defaultTo(0, value);

    props.formApi.clearErrors('quantity');

    const validationResult = quantitySchema.safeParse(valueToValidate);

    if (validationResult.success) {
      return;
    }

    const errorMessages = validationResult.error?.errors.map((error) => error.message).join(' ');
    props.formApi.setError('quantity', {message: errorMessages});
  };

  const handleQuantityChange = (value: number | null) => {
    if (isNil(value)) {
      return;
    }

    validateQuantity(value);

    const sellingPricePerUnit = props.formApi.getValues('sellingPricePerUnit');

    props.formApi.setValue(
      'sellingPrice',
      isNotNil(sellingPricePerUnit) ? value * sellingPricePerUnit : 0
    );

    const mechanics = props.formApi.getValues('assignMechanics');

    if (mechanics?.length === 1) {
      return props.formApi.setValue(`assignMechanics.0.amount`, value);
    }

    mechanics?.forEach(
      (mechanic, index) =>
        isNotNil(mechanic.amount) &&
        props.formApi.setValue(
          `assignMechanics.${index}.ratio`,
          getRatioFromAmount(mechanic.amount, value) ?? 0
        )
    );
  };

  const handleCustomPriceSwitch = (value: boolean) => {
    if (value) {
      props.formApi.setValue('customPriceType', 'perUnit');
      return;
    }

    const quantity = props.formApi.getValues('quantity');

    props.formApi.setValue(
      'sellingPrice',
      isNotNil(quantity) && isNotNil(itemData?.baseSellingPricePerUnit?.amount)
        ? quantity * itemData.baseSellingPricePerUnit.amount
        : 0
    );
    props.formApi.setValue('sellingPricePerUnit', itemData?.baseSellingPricePerUnit?.amount ?? 0);
    props.formApi.setValue('purchasePricePerUnit', itemData?.basePurchasePricePerUnit?.amount);
    props.formApi.setValue('vatType', itemData?.baseSellingPriceVat?.type ?? '');
  };

  const handleCustomPricePerUnitChange = (value: number | Nullish) => {
    const quantity = props.formApi.getValues('quantity');

    props.formApi.setValue(
      'sellingPrice',
      isNotNil(quantity) && isNotNil(value) ? value * quantity : 0
    );
  };

  const handleCustomPriceChange = (value: number | Nullish) => {
    const quantity = props.formApi.getValues('quantity');

    props.formApi.setValue(
      'sellingPricePerUnit',
      isNotNil(quantity) && isNotNil(value)
        ? precisionCalculation.round(precisionCalculation.divide(value, quantity), 2)
        : 0
    );
  };

  const itemPriceDiscountRate = getItemPriceDiscountRate({
    isCustomDiscount: formValues.isCustomDiscount,
    discountType: formValues.discountType,
    discountRate: formValues.discountRate,
    itemData,
  });

  return (
    <VStack spacing={4}>
      <Show when={hasGoodwill}>
        <Alert
          variant="info"
          type="inline"
          title={i18n.t('entity.addWork.lables.itemHasGoodwill')}
          message={i18n.t('entity.addWork.lables.goodwillRatio', {
            ratio: goodwillRatio * GOODWILL_RATIO_PERCENTAGE,
          })}
          data-testid={suffixTestId('itemHasGoodwill', props)}
        />
      </Show>
      <Card variant="inlineGrey">
        <VStack spacing={4}>
          <Grid spacing={4} columns={8}>
            <GridItem span={4}>
              <HStack spacing={4}>
                <Box width={13}>
                  <FormField
                    isDisabled
                    control={props.control}
                    type="text"
                    name="sNo"
                    label={i18n.t('entity.warehouse.labels.sNo')}
                  />
                </Box>
                <Box flex={1}>
                  <FormField
                    isDisabled={hasGoodwill}
                    control={props.control}
                    type="text"
                    name="number"
                    label={i18n.t('general.labels.number')}
                    data-testid={suffixTestId('number', props)}
                  />
                </Box>
              </HStack>
            </GridItem>
            <GridItem span={4}>
              <FormField
                isRequired
                isDisabled={hasGoodwill}
                control={props.control}
                name="name"
                type="text"
                label={i18n.t('general.labels.name')}
                tooltip={getSourceNameTooltip(itemData?.name)}
                data-testid={suffixTestId('name', props)}
              />
            </GridItem>
          </Grid>

          <Grid spacing={4} columns={8}>
            <GridItem span={2}>
              <FormField
                isDisabled={hasGoodwill}
                isNotClearable
                control={props.control}
                name="workType"
                type="choice"
                label={i18n.t('entity.addWork.lables.workCategory')}
                placeholder={i18n.t('general.labels.select')}
                options={workTypeOptions}
                onChange={handleWorkTypeChange}
                menuInPortal
                data-testid={suffixTestId('workType', props)}
              />
            </GridItem>
            <GridItem span={2}>
              <FormField
                isDisabled={hasGoodwill || not(isTimeNormType)}
                isNotClearable
                control={props.control}
                name="labourRateId"
                type="choice"
                label={i18n.t('entity.addWork.lables.defaultHourlyRate')}
                options={props.labourRatesOptions}
                onChange={handleLabourRateChange}
                menuInPortal
                data-testid={suffixTestId('labourRateId', props)}
              />
            </GridItem>
            <GridItem span={2}>
              <FormField
                name="workItemGroupId"
                control={props.control}
                type="choice"
                options={props.workItemGroupOptions}
                label={i18n.t('entity.warehouse.labels.workItemGroup')}
                menuInPortal
                data-testid={suffixTestId('workItemGroupId', props)}
              />
            </GridItem>
            <Show when={isCalculationPriceType || isDirectPriceType}>
              <GridItem span={1}>
                <FormField
                  name="timeOutputPerUnit"
                  control={props.control}
                  type="number"
                  label={i18n.t('entity.addWork.lables.timeOutputPerUnit')}
                  suffix={i18n.t('general.labels.hourCompact')}
                  data-testid={suffixTestId('timeOutputPerUnit', props)}
                />
              </GridItem>
            </Show>
            <GridItem span={isCalculationPriceType || isDirectPriceType ? 1 : 2}>
              <FormField
                isDisabled
                isNotClearable
                control={props.control}
                name="priceType"
                type="choice"
                label={i18n.t('entity.warehouse.labels.labourType')}
                options={labourTypesOptions}
              />
            </GridItem>
          </Grid>

          <Separator spacing={0} />

          <EditLabourItemMechanics
            control={props.control}
            formApi={props.formApi}
            serviceCaseId={props.serviceCaseId}
            serviceOrderId={props.serviceOrderId}
            serviceJobId={props.serviceJobId}
            data-testid={suffixTestId('assignMechanics', props)}
            isDisabled={props.isOrderReadOnly}
          />

          <Separator spacing={0} />

          <VStack spacing={4}>
            <HStack spacing={4}>
              <Hide when={isCooperationType || isSubcontractPriceType}>
                <Box flex={1}>
                  <FormField
                    name="quantity"
                    control={props.control}
                    type="number"
                    label={i18n.t('entity.order.itemQuantity')}
                    isDisabled={isDirectPriceType || hasGoodwill}
                    onChange={handleQuantityChange}
                    suffix={itemData?.quantity?.unit}
                    data-testid={suffixTestId('quantity', props)}
                  />
                </Box>
              </Hide>
              <Show when={isTimeNormType}>
                <Box flex={1}>
                  <FormField
                    name="originalSellingPricePerUnit"
                    type="number"
                    isDisabled
                    control={props.control}
                    label={i18n.t('entity.order.itemPurchasePrice')}
                    suffix={tenant.tenantCurrency}
                    data-testid={suffixTestId('originalSellingPricePerUnit', props)}
                  />
                </Box>
              </Show>
              <Hide when={isTimeNormType}>
                <Box flex={1}>
                  <FormField
                    name="originalPurchasePricePerUnit"
                    type="number"
                    control={props.control}
                    label={
                      isCooperationType || isSubcontractPriceType
                        ? i18n.t('general.labels.purchasePrice')
                        : i18n.t('entity.addWork.lables.purchasePricePerUnit')
                    }
                    isDisabled
                    suffix={tenant.tenantCurrency}
                    data-testid={suffixTestId('originalPurchasePricePerUnit', props)}
                  />
                </Box>
                <Box flex={1}>
                  <FormField
                    name="originalSellingPricePerUnit"
                    type="number"
                    control={props.control}
                    label={
                      isCooperationType || isSubcontractPriceType
                        ? i18n.t('general.labels.sellingPrice')
                        : i18n.t('entity.addWork.lables.sellingPricePerUnit')
                    }
                    isDisabled
                    suffix={tenant.tenantCurrency}
                    data-testid={suffixTestId('originalSellingPricePerUnit', props)}
                  />
                </Box>
              </Hide>
              <Box flex={1}>
                <FormField
                  isNotClearable
                  control={props.control}
                  type="choice"
                  name="vatType"
                  options={vatOptions}
                  label={i18n.t('entity.addWork.lables.vat')}
                  placeholder={i18n.t('general.labels.select')}
                  isDisabled={
                    hasGoodwill ||
                    not(formValues.isCustomPrice) ||
                    isFalse(itemData?.sellingPriceVat?.isEditingAllowed)
                  }
                  menuInPortal
                  data-testid={suffixTestId('vatType', props)}
                />
              </Box>
              <Show when={isTimeNormType || isCooperationType || isSubcontractPriceType}>
                <Box flex={1} />
              </Show>
            </HStack>
            <Box maxWidth="fit-content">
              <FormField
                name="isCustomPrice"
                type="switch"
                control={props.control}
                label={i18n.t('entity.orderItem.labels.customPrice')}
                onChange={handleCustomPriceSwitch}
                isDisabled={
                  isFalse(itemData?.isCustomPriceEditingAllowed) || !props.canEditCustomPrice
                }
                data-testid={suffixTestId('isCustomPrice', props)}
              />
            </Box>
            <Show when={formValues.isCustomPrice && isTimeNormType}>
              <Grid columns={4}>
                <RadioItem
                  value={formValues.customPriceType === 'perUnit'}
                  isDisabled={!props.canEditCustomPrice || props.isOrderReadOnly}
                  onChange={() => props.formApi.setValue('customPriceType', 'perUnit')}
                >
                  {i18n.t('entity.warehouse.labels.pricePerUnit')}
                </RadioItem>
                <FormField
                  control={props.control}
                  type="number"
                  name="sellingPricePerUnit"
                  isDisabled={
                    formValues.customPriceType !== 'perUnit' ||
                    isFalse(itemData?.sellingPricePerUnit?.isEditingAllowed) ||
                    !props.canEditCustomPrice
                  }
                  onChange={
                    formValues.customPriceType === 'perUnit'
                      ? handleCustomPricePerUnitChange
                      : undefined
                  }
                  suffix={tenant.tenantCurrency}
                  data-testid={suffixTestId('sellingPricePerUnit', props)}
                />
                <RadioItem
                  value={formValues.customPriceType === 'sellingPrice'}
                  isDisabled={!props.canEditCustomPrice || props.isOrderReadOnly}
                  onChange={() => props.formApi.setValue('customPriceType', 'sellingPrice')}
                >
                  {i18n.t('general.labels.sellingPrice')}
                </RadioItem>
                <FormField
                  control={props.control}
                  type="number"
                  name="sellingPrice"
                  isDisabled={
                    formValues.customPriceType !== 'sellingPrice' ||
                    isFalse(itemData?.sellingPricePerUnit?.isEditingAllowed) ||
                    !props.canEditCustomPrice
                  }
                  onChange={
                    formValues.customPriceType === 'sellingPrice'
                      ? handleCustomPriceChange
                      : undefined
                  }
                  suffix={tenant.tenantCurrency}
                  data-testid={suffixTestId('sellingPrice', props)}
                />
              </Grid>
            </Show>
            <Show when={formValues.isCustomPrice && !isTimeNormType}>
              <Grid columns={4}>
                <FormField
                  control={props.control}
                  label={i18n.t(
                    isCalculationPriceType
                      ? 'entity.addWork.lables.purchasePricePerUnit'
                      : 'general.labels.purchasePrice'
                  )}
                  type="number"
                  name="purchasePricePerUnit"
                  isDisabled={
                    isFalse(itemData?.purchasePricePerUnit?.isEditingAllowed) ||
                    !props.canEditCustomPrice
                  }
                  suffix={tenant.tenantCurrency}
                  data-testid={suffixTestId('purchasePricePerUnit', props)}
                />
                <FormField
                  control={props.control}
                  label={i18n.t(
                    isCalculationPriceType
                      ? 'entity.addWork.lables.sellingPricePerUnit'
                      : 'general.labels.sellingPrice'
                  )}
                  type="number"
                  name="sellingPricePerUnit"
                  isDisabled={
                    isFalse(itemData?.sellingPricePerUnit?.isEditingAllowed) ||
                    !props.canEditCustomPrice
                  }
                  suffix={tenant.tenantCurrency}
                  data-testid={suffixTestId('sellingPricePerUnit', props)}
                />
              </Grid>
            </Show>
            <HStack justify="space-between" align="center">
              <FormField
                control={props.control}
                type="switch"
                name="isCustomDiscount"
                label={i18n.t('general.labels.discount')}
                isDisabled={
                  isFalsy(itemData?.discount?.isEditingAllowed) || !props.canEditManualDiscount
                }
                data-testid={suffixTestId('discount', props)}
              />
              <FormField
                control={props.control}
                name="isDoNotApplyDiscount"
                type="checkbox"
                isDisabled={isFalsy(itemData?.isDoNotApplyDiscountEditable)}
                label={i18n.t('general.labels.doNotApplyDiscount')}
                data-testid={suffixTestId('doNotApplyDiscount', props)}
              />
            </HStack>
            <Show
              when={formValues.isCustomDiscount && not(itemData?.discount?.isDoNotApplyDiscount)}
            >
              <Grid columns={4}>
                {DISCOUNT_SOURCE_TYPES.map((sourceType) => {
                  const availableDiscount = itemData?.discount?.availableDiscounts?.find(
                    (discount) => discount?.sourceType === sourceType
                  );

                  return (
                    <Show key={sourceType} when={availableDiscount}>
                      <RadioItem
                        value={formValues.discountType === sourceType}
                        isDisabled={!props.canEditManualDiscount || props.isOrderReadOnly}
                        onChange={() =>
                          props.formApi.setValue('discountType', sourceType, {
                            shouldDirty: true,
                          })
                        }
                      >
                        {getDiscountSourceLabel(sourceType)}
                      </RadioItem>
                      {sourceType === 'manual-discount' ? (
                        <FormField
                          control={props.control}
                          type="number"
                          name="discountRate"
                          decimalPlaces={2}
                          suffix="%"
                          maxStepperValue={100}
                          minStepperValue={0}
                          isDisabled={
                            formValues.discountType !== 'manual-discount' ||
                            !props.canEditManualDiscount
                          }
                          data-testid={suffixTestId(getDiscountSourceTestId(sourceType), props)}
                        />
                      ) : (
                        <NumberInput
                          isDisabled
                          value={getPercentageFromDecimal(availableDiscount?.rate) ?? 0}
                          suffix="%"
                          data-testid={suffixTestId(getDiscountSourceTestId(sourceType), props)}
                        />
                      )}
                    </Show>
                  );
                })}
              </Grid>
            </Show>
          </VStack>
        </VStack>
      </Card>
      <ItemPrice
        itemId={props.itemId}
        serviceCaseId={props.serviceCaseId}
        serviceOrderId={props.serviceOrderId}
        serviceJobId={props.serviceJobId}
        amount={formValues.quantity}
        purchasePricePerUnit={formValues.purchasePricePerUnit}
        purchaseCurrency={itemData?.purchasePricePerUnit?.currency}
        sellingPricePerUnit={formValues.sellingPricePerUnit}
        sellingCurrency={itemData?.sellingPricePerUnit?.currency}
        vatType={formValues.vatType}
        priceType={itemData?.priceType ?? ''}
        workType={formValues.workType}
        assignMechanics={formValues.assignMechanics}
        discountRate={itemPriceDiscountRate}
        showMargin={props.canReadServiceOrderFinancesLabourItem}
        showPurchasePrice={props.canReadServiceOrderFinancesLabourItem}
        isDisabled={props.isOrderReadOnly}
      />
      <DialogFooter>
        <ButtonGroup align="right">
          <Button
            onClick={props.onClose}
            variant="secondary"
            title={i18n.t('general.actions.discard')}
            data-testid={suffixTestId('discard', props)}
          />
          <FormButton
            isDisabled={!isTouched || isFetchingLabourRates}
            control={props.control}
            type="submit"
            title={i18n.t('general.actions.saveChanges')}
            isLoading={props.isPatchLoading || isFetchingLabourRates}
            data-testid={suffixTestId('save', props)}
          />
        </ButtonGroup>
      </DialogFooter>
    </VStack>
  );
}

const labourTypesOptions = [
  {
    label: getLabourTypeLabel(LabourTypeEnum.timeNorm),
    value: LabourTypeEnum.timeNorm,
  },
  {
    label: getLabourTypeLabel(LabourTypeEnum.cooperation),
    value: LabourTypeEnum.cooperation,
  },
  {
    label: getLabourTypeLabel(LabourTypeEnum.directPrice),
    value: LabourTypeEnum.directPrice,
  },
  {
    label: getLabourTypeLabel(LabourTypeEnum.calculationPrice),
    value: LabourTypeEnum.calculationPrice,
  },
  {
    label: getLabourTypeLabel(LabourTypeEnum.subcontract),
    value: LabourTypeEnum.subcontract,
  },
];

const quantitySchema = z.number().positive();

const DISCOUNT_SOURCE_TYPES = ['customer-contract', 'custom-discounts', 'manual-discount'] as const;

type DiscountSourceType = (typeof DISCOUNT_SOURCE_TYPES)[number];

const getDiscountSourceLabel = (sourceType: DiscountSourceType) =>
  match(sourceType)
    .with('customer-contract', () => i18n.t('entity.customerContract.labels.customerContract'))
    .with('custom-discounts', () => i18n.t('entity.orderItem.labels.customDiscount'))
    .with('manual-discount', () => i18n.t('entity.orderItem.labels.manualDiscount'))
    .exhaustive();

const getDiscountSourceTestId = (sourceType: DiscountSourceType) =>
  match(sourceType)
    .with('customer-contract', () => 'customerContract')
    .with('custom-discounts', () => 'customDiscount')
    .with('manual-discount', () => 'discount')
    .exhaustive();
